import React, { useEffect, useState, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import MapWithList from "./MapWithList";
import alert from "../assets/alert.mp3";
import moment from "moment";

import { db } from "../firebase";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
  updateDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import Loading from "./Loading";
import useInterval from "../hooks/useInterval";

import { ALARM_LIMIT_IN_SEC } from "./helpers"
import { schema } from "../schema";

function diffInSecondsFromNow(dateInSeconds) {
  const docDateInMilsec = dateInSeconds * 1000;
  const nowInMilsec = (new Date).getTime();
  return Math.floor((nowInMilsec - docDateInMilsec) / 1000);
}

const Facility = () => {
  const { id } = useParams();

  const [facility, setFacility]       = useState(null)
  const [zones, setZones]             = useState(null)
  const [facilityData, setFacilityData] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [operators, setOperators]     = useState([]);   // from facility.operators field
  const [knownGuards, setKnownGuards] = useState([]);   // from Facilities/{id}/guards subcollection
  const [soundBlocked, setSoundBlocked] = useState(false); // true if the browser blocks the alert audio (autoplay policy)

  const rawZonesIds = zones?.reduce((acc, z) => {
    if (schema.numericIds) {
      const num = Number(z.id);
      if (!isNaN(num)) acc.push(num);
      acc.push(String(z.id));
    } else {
      acc.push(z.id);
    }
    return acc;
  }, []) || [];
  const zonesIds = [...new Set(rawZonesIds)];

  const resolveAlarm = () => {
    if (!facility) return;
    // Cierre configurable o default 8 horas
    const secondsToClose = facility.secondsToCloseAlarm || (8 * 3600);
    const alarmsToBeClosed = facilityData.filter(d => !d.resolvedAt && diffInSecondsFromNow(d.createdAt.seconds) > secondsToClose);

    const updateDocument = async (id) => {
      const alarmRef = doc(db, schema.groupedAlarms, id);

      // Formatear tiempo para la nota
      let timeStr = (secondsToClose >= 3600)
        ? `${Math.round(secondsToClose / 3600)} horas`
        : `${Math.round(secondsToClose / 60)} minutos`;

      const message = `🤖 Ataque cerrado automáticamente (Sin gestión por > ${timeStr}).`;

      await updateDoc(alarmRef, {
        notes: message,
        situation: "8",
        closedBy: "Sistema",
        reasonForResolution: "resolved_automatically",
        closeResult: "unmanaged",
        closedAt: serverTimestamp(),
        resolvedAt: serverTimestamp(),
      });
    }

    if (alarmsToBeClosed.length > 0 && facility.autoCloseAlarms) {
      alarmsToBeClosed.forEach(d => updateDocument(d.docID));
    }
  };
  useInterval(resolveAlarm, 60000);

  // Load facility document
  useEffect(() => {
    if (facility === null) {
      const facilityRef = doc(db, `Facilities/${id}`);
      const q = query(facilityRef);

      const unsub = onSnapshot(q, (snapshot) => {
        const data = snapshot.data();
        setFacility(data);
        // Extract operators list from facility document (new field)
        if (Array.isArray(data?.operators)) {
          setOperators(data.operators);
        }
        // Extract guards list from facility document
        if (Array.isArray(data?.guards)) {
          setKnownGuards(data.guards.sort());
        } else {
          setKnownGuards([]);
        }
      }, (error) => {
        console.error("Facility load error:", error);
        setLoading(false);
      });
      return unsub;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load zones
  useEffect(() => {
    if (zones === null) {
      const zonesRef = collection(db, `Facilities/${id}/zones`);
      const q = query(zonesRef);

      const unsub = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(d => ({ ...d.data(), id: d.id }))
        setZones(docs)
        if (snapshot.empty) {
          setLoading(false)
        }
      }, (error) => {
        console.error("Zones load error:", error);
        setLoading(false);
      });
      return unsub;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load alarms
  useEffect(() => {
    if (zones !== null && zonesIds.length > 0) {
      const alarmsRef = collection(db, schema.groupedAlarms);
      
      const chunkedZones = [];
      for (let i = 0; i < zonesIds.length; i += 30) {
        chunkedZones.push(zonesIds.slice(i, i + 30));
      }

      let chunksData = new Array(chunkedZones.length).fill([]);

      const unsubs = chunkedZones.map((chunk, index) => {
        const q = query(
          alarmsRef,
          where(schema.zoneField, "in", chunk),
          orderBy("updatedAt", "desc"),
          limit(100)
        );

        return onSnapshot(q, (snapshot) => {
          const formattedAlarms =
            snapshot.docs.map(doc => {
              const lastDate =
                doc.data().resolvedAt ?
                  new Date(doc.data().resolvedAt.seconds * 1000) :
                  new Date()

              return {
                ...doc.data(),
                lastDate,
                resolvedAt: doc.data().resolvedAt,
                date: new Date((doc.data().updatedAt?.seconds || 0) * 1000),
                zoneName: zones.find((f) => String(f.id) === String(doc.data()[schema.zoneField]))?.zoneName || `Zona ${doc.data()[schema.zoneField]}`,
                docID: doc.id,
                createdAtLocalTime: moment((doc.data().createdAt?.seconds || 0) * 1000).format('LT'),
                createdAtDate: new Date((doc.data().createdAt?.seconds || 0) * 1000),
              }
            })

          chunksData[index] = formattedAlarms;

          const mergedAlarms = chunksData.flat().sort((a, b) => {
            const timeA = a.updatedAt?.seconds || 0;
            const timeB = b.updatedAt?.seconds || 0;
            return timeB - timeA;
          }).slice(0, 100);

          const isAlarmInTime = alarmHour => {
            if (!facility?.visibleHoursRange) return true;
            const [start, end] = facility.visibleHoursRange;
            if (start <= end) {
              return alarmHour >= start && alarmHour < end;
            } else {
              return alarmHour >= start || alarmHour < end;
            }
          }

          const filteredFormattedAlarms =
            facility.visibleHoursRange === undefined ?
              mergedAlarms :
              mergedAlarms.filter(alarm => isAlarmInTime(alarm.createdAtDate.getHours()))

          setFacilityData(filteredFormattedAlarms)
          setLoading(false)

          snapshot.docChanges().forEach((change) => {
            const createdAtSeconds = change.doc.data().createdAt?.seconds;
            if (!createdAtSeconds) return;
            const newAlarmCreatedAt = new Date(createdAtSeconds * 1000)
            const isNewAllarm = diffInSecondsFromNow(createdAtSeconds) < ALARM_LIMIT_IN_SEC;

            if (isNewAllarm && change.type === "added" && isAlarmInTime(newAlarmCreatedAt.getHours())) {
              new Audio(alert).play()
                .then(() => setSoundBlocked(false))
                .catch((err) => {
                  console.log("Audio play blocked by browser autoplay policy:", err);
                  setSoundBlocked(true); // surface the "activar sonido" banner
                });
            }
          });
        }, (error) => {
          console.error("Alarms load error:", error);
          setLoading(false);
        });
      });
      return () => unsubs.forEach(unsub => unsub());
    }

  }, [zones]);

  // Auto-cierre one-shot: eventos >2 días sin despachar
  const autoCloseRan = useRef(false);
  useEffect(() => {
    if (zones === null || zonesIds.length === 0 || autoCloseRan.current) return;
    autoCloseRan.current = true;

    async function closeOldUnmanaged() {
      const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      const alarmsRef = collection(db, schema.groupedAlarms);

      // Procesar por chunks de 30 zonas (límite de Firestore para "in")
      const chunks = [];
      for (let i = 0; i < zonesIds.length; i += 30) {
        chunks.push(zonesIds.slice(i, i + 30));
      }

      for (const chunk of chunks) {
        try {
          const q = query(
            alarmsRef,
            where(schema.zoneField, "in", chunk),
            orderBy("updatedAt", "desc"),
            limit(500)
          );
          const snap = await getDocs(q);

          const batch = [];
          snap.docs.forEach(d => {
            const data = d.data();
            // Solo cerrar si: no resuelto, no despachado, y >2 días de antigüedad
            if (!data.resolvedAt && !data.dispatchedBy && data.createdAt?.seconds) {
              const ageMs = now - (data.createdAt.seconds * 1000);
              if (ageMs > TWO_DAYS_MS) {
                batch.push(
                  updateDoc(doc(db, schema.groupedAlarms, d.id), {
                    resolvedAt: serverTimestamp(),
                    situation: "8",
                    reasonForResolution: "resolved_automatically",
                    closeResult: "unmanaged",
                    closedBy: "Sistema",
                    closedAt: serverTimestamp(),
                  })
                );
              }
            }
          });

          if (batch.length > 0) {
            await Promise.all(batch);
            console.log(`[AutoCloser] Cerrados ${batch.length} eventos antiguos sin despachar.`);
          }
        } catch (err) {
          console.warn("[AutoCloser] Error:", err);
        }
      }
    }

    closeOldUnmanaged();
  }, [zones, zonesIds]);

  // Restart listener
  useEffect(() => {
    if (zones !== null) {
      const restartRef = collection(db, "WebAppRestarts");
      const q = query(
        restartRef,
        where("FacilityID", "==", id),
        orderBy("Datetime", "desc"),
        limit(1)
      );

      const unsub = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const docDateInMilsec = change.doc.data().Datetime.seconds * 1000;
          const nowInMilsec = (new Date).getTime();
          const diffTimeInSec = (nowInMilsec - docDateInMilsec) / 1000;
          const isNewRestart = Math.floor(diffTimeInSec) < 4;
          if (isNewRestart && change.type === "added") {
            window.location.assign("/")
          }
        });
      });
      return unsub;
    }

  }, [zones]);

  // Proactive check: some Chrome versions expose the autoplay policy up front,
  // so we can prompt the operator to enable sound BEFORE the first alarm arrives.
  useEffect(() => {
    if (typeof navigator.getAutoplayPolicy === "function") {
      try {
        if (navigator.getAutoplayPolicy("mediaelement") !== "allowed") {
          setSoundBlocked(true);
        }
      } catch (e) { /* API quirk — fall back to the reactive path */ }
    }
  }, []);

  // Called from a real user gesture (banner click). Playing within a gesture
  // grants document-wide sticky activation, so later programmatic alarms play too.
  const enableSound = () => {
    new Audio(alert).play()
      .then(() => setSoundBlocked(false))
      .catch((err) => console.log("No se pudo activar el sonido:", err));
  };

  if (!loading && !facility) return <Navigate to={`/`} />;

  if (loading) return (
    <div className="flex justify-center items-center h-screen" style={{ background: "var(--bg)" }}>
      <Loading size="14" />
    </div>
  )
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      {soundBlocked && (
        <div
          onClick={enableSound}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") enableSound(); }}
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999,
            background: "#f59e0b", color: "#1a1a1a",
            padding: "10px 16px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            cursor: "pointer", fontSize: 13, fontWeight: 700,
            boxShadow: "0 -2px 12px rgba(0,0,0,0.35)",
            borderTop: "1px solid #b45309",
          }}
        >
          <span style={{ animation: "pulse 1.5s infinite" }}>🔔</span>
          <span>Sonido de alerta desactivado por el navegador — Haz clic aquí para activarlo</span>
        </div>
      )}
      <div className="max-w-[1800px] mx-auto">
        <MapWithList
          title={facility.title}
          alarms={facilityData}
          facility={{ ...facility, id }}
          zones={zones}
          operators={operators}
          knownGuards={knownGuards}
        />
      </div>
    </div>
  );
};

export default Facility;
